import { createContext, useContext, useEffect, useState } from "react";
import { Link, Navigate, NavLink, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { BarChart3, CalendarDays, Inbox, LayoutDashboard, LogOut, Settings, Users } from "lucide-react";
import { api, setToken } from "@/admin/api";
import Login from "@/admin/pages/Login";
import Overview from "@/admin/pages/Overview";
import Enquiries from "@/admin/pages/Enquiries";
import Calendar from "@/admin/pages/Calendar";
import Jobs from "@/admin/pages/Jobs";
import JobDetail from "@/admin/pages/JobDetail";
import Revenue from "@/admin/pages/Revenue";
import SettingsPage from "@/admin/pages/Settings";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/enquiries", label: "Enquiries", icon: Inbox },
  { to: "/admin/calendar", label: "Calendar", icon: CalendarDays },
  { to: "/admin/jobs", label: "Clients & Jobs", icon: Users },
  { to: "/admin/revenue", label: "Revenue", icon: BarChart3 },
];

const Shell = ({ children }) => {
  const { user, logout } = useAuth();
  const link = ({ isActive }) =>
    `flex items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold transition-colors ${isActive ? "bg-brand text-white" : "text-navy hover:bg-alt"}`;
  const tab = ({ isActive }) =>
    `flex min-h-14 flex-1 flex-col items-center justify-center gap-0.5 text-[11px] font-semibold ${isActive ? "text-brand" : "text-body"}`;
  return (
    <div data-testid="admin-shell" className="min-h-screen bg-alt text-navy md:flex">
      <aside className="hidden w-64 shrink-0 flex-col border-r border-line bg-white p-5 md:flex">
        <Link to="/admin" className="mb-8 block">
          <img src="/images/dms-home-improvements-logo.webp" alt="DMS Home Improvements" width="600" height="213" className="h-11 w-auto" />
        </Link>
        <nav className="flex flex-1 flex-col gap-1">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} className={link} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <n.icon size={20} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <NavLink to="/admin/settings" className={link} data-testid="nav-settings">
          <Settings size={20} /> Settings
        </NavLink>
        <button type="button" onClick={logout} data-testid="logout-button" className="mt-1 flex items-center gap-3 rounded-xl px-4 py-3 text-base font-semibold text-body hover:bg-alt">
          <LogOut size={20} /> Log out
        </button>
        <p className="mt-4 truncate px-4 text-xs text-body">{user?.email}</p>
      </aside>

      <header className="sticky top-0 z-40 flex h-16 items-center justify-between border-b border-line bg-white px-4 md:hidden">
        <Link to="/admin"><img src="/images/dms-home-improvements-logo.webp" alt="DMS Home Improvements" width="600" height="213" className="h-9 w-auto" /></Link>
        <Link to="/admin/settings" aria-label="Settings" data-testid="mobile-settings-link" className="flex h-11 w-11 items-center justify-center rounded-full hover:bg-alt">
          <Settings size={22} />
        </Link>
      </header>

      <main className="mx-auto w-full max-w-5xl flex-1 px-4 pb-28 pt-5 md:px-8 md:pb-12 md:pt-8">{children}</main>

      <nav data-testid="mobile-tabs" className="fixed inset-x-0 bottom-0 z-40 flex border-t border-line bg-white pb-[env(safe-area-inset-bottom)] md:hidden">
        {NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} className={tab} data-testid={`tab-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
            <n.icon size={22} /> {n.label.replace("Clients & ", "")}
          </NavLink>
        ))}
      </nav>
    </div>
  );
};

export default function AdminApp() {
  const [user, setUser] = useState(null); // null = checking, false = logged out
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = "Dashboard | DMS Home Improvements";
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.appendChild(meta);
    api("/auth/me").then(setUser).catch(() => setUser(false));
    return () => meta.remove();
  }, []);

  const login = async (email, password) => {
    const data = await api("/auth/login", { method: "POST", body: { email, password } });
    setToken(data.access_token);
    setUser(data.user);
    navigate("/admin", { replace: true });
  };
  const logout = async () => {
    await api("/auth/logout", { method: "POST" }).catch(() => {});
    setToken(null);
    setUser(false);
    navigate("/admin/login", { replace: true });
  };

  if (user === null) return <p className="p-10 text-center text-body">Loading…</p>;

  return (
    <AuthCtx.Provider value={{ user, login, logout, setUser }}>
      <Routes>
        <Route path="login" element={user ? <Navigate to="/admin" replace /> : <Login />} />
        <Route
          path="*"
          element={
            user ? (
              <Shell>
                <Routes>
                  <Route index element={<Overview />} />
                  <Route path="enquiries" element={<Enquiries />} />
                  <Route path="calendar" element={<Calendar />} />
                  <Route path="jobs" element={<Jobs />} />
                  <Route path="jobs/:id" element={<JobDetail />} />
                  <Route path="revenue" element={<Revenue />} />
                  <Route path="settings" element={<SettingsPage />} />
                  <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
              </Shell>
            ) : (
              <Navigate to="/admin/login" replace state={{ from: location.pathname }} />
            )
          }
        />
      </Routes>
    </AuthCtx.Provider>
  );
}
